import React, { useMemo } from 'react';

import { equals, pick } from 'ramda';
import { Field, Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import FormSection from 'components/Layouts/Form/FormSection';
import LayoutWithFooter from 'components/Layouts/shared/LayoutWithFooter';
import ArrayFields from 'components/shared/ArrayFields';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import FormErrorText from 'components/shared/FormErrorText';
import Loader from 'components/shared/Loader';
import FormTitle from 'components/shared/Typography/FormTitle';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import useElementBottomPosition, { SNACKBAR_POSITION } from 'hooks/useElementBottomPosition';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import { privacyPolicyFilledSelector } from 'store/user/selectors';

import ItinerariesItemActionButtons from '../../ItinerariesItemBuilder/ActionButtons';
import { BuilderFooterButton } from '../../PageTypes/shared/BuilderFooterButton';
import { FormFieldKeys } from './constants';
import { FormUpdateWatcher } from './FormUpdateWatcher';
import { useGuestHandlers } from './functions';
import GuestFormBlockedOverlay from './GuestFormBlockedOverlay';
import GuestsAdapter from './GuestsAdapter';
import styles from './index.module.css';

function GuestForms() {
  useScrollPageToTop();
  useElementBottomPosition(SNACKBAR_POSITION);
  const [isSaving, setIsSaving] = React.useState(false);

  const {
    guestPortal,
    handleNext,
    mainFormState,
    handleUpdateMainFormState,
    handleSaveAllForm,

    handleDeleteGuest,
    handleClickEditGuestButton,
    handleClickAddNewGuestButton,
    handleClickGuestSearch,
  } = useGuestHandlers();

  const isGuestFormsBlocked = !useSelector(privacyPolicyFilledSelector) && guestPortal.worksWithNewPrivacyPolicy;

  const initialFormValues = useMemo(() => {
    const formFieldsKeys = Object.values(FormFieldKeys);
    return pick(formFieldsKeys, guestPortal);
  }, [guestPortal]);

  const handleSubmit = async (values) => {
    await handleNext(values);
  };

  return (
    <LayoutWithFooter>
      {({ footerComponent: FooterComponent }) => (
        <div>
          <FormSection>
            <Form onSubmit={handleSubmit} initialValues={initialFormValues}>
              {({ handleSubmit, submitting, submitError, invalid }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <UnsavedChangesFormWatcher />
                    {submitting && <Loader isFillBlock />}

                    <FormUpdateWatcher onUpdate={handleUpdateMainFormState} />

                    {isGuestFormsBlocked && <GuestFormBlockedOverlay />}
                    <FormTitle
                      className={styles.noBorder}
                      hint="Add guests and fill out guest information below. Guests can fill this out in the guest portal as well too."
                    >
                      I. Guest List
                    </FormTitle>

                    <Field
                      name="guests"
                      isEqual={equals}
                      component={({ input }) => (
                        <GuestsAdapter
                          value={input.value}
                          onChange={input.onChange}
                          handleDeleteGuest={handleDeleteGuest}
                          handleClickEditGuestButton={handleClickEditGuestButton}
                          handleClickAddNewGuestButton={handleClickAddNewGuestButton}
                          handleClickGuestSearch={handleClickGuestSearch}
                        />
                      )}
                    />

                    <FormTitle hint="Are there any special occasions you’ll be celebrating during your trip?">
                      II. Special Occasions
                    </FormTitle>

                    <Field
                      component={FormGroupAdapter}
                      name={FormFieldKeys.specialOccasions}
                      placeholder="25th Birthday"
                      type="textarea"
                      minRows={2}
                      parse={(value) => value || null}
                    />

                    <FormTitle hint="Add custom inputs for guest below.">III. Additional Info</FormTitle>
                    <ArrayFields
                      initialValues={initialFormValues}
                      data={{
                        sectionName: FormFieldKeys.additionalInfo,
                        fieldsData: [
                          {
                            name: 'question',
                            placeholder: 'Type question here...',
                            type: 'textarea',
                            minRows: 2,
                          },
                          {
                            name: 'answer',
                            placeholder: 'Type answer here...',
                            type: 'textarea',
                            minRows: 2,
                          },
                        ],
                      }}
                    />

                    {submitError && <FormErrorText>{submitError}</FormErrorText>}

                    <FooterComponent>
                      <FormActions isRight>
                        <BuilderFooterButton loading={submitting} disabled={invalid || isSaving} onClick={handleSubmit}>
                          Next
                        </BuilderFooterButton>
                      </FormActions>
                    </FooterComponent>
                  </form>
                );
              }}
            </Form>
          </FormSection>

          <ItinerariesItemActionButtons
            onSave={handleSaveAllForm}
            hasValidationErrors={mainFormState.hasValidationErrors}
            submitting={mainFormState.submitting}
            onIsSavingChange={setIsSaving}
          />
        </div>
      )}
    </LayoutWithFooter>
  );
}

export default GuestForms;
